import { ASSESSMENT_WORDS } from '@/data/assessmentWords';
import { M2_ASSESSMENT_WORDS } from '@/data/m2-assessmentWords';
import { M3_ASSESSMENT_WORDS } from '@/data/m3-assessmentWords';
import { Word } from '@/types/assessmentTypes';

export interface CategorizedWord {
  translationKey: string;
  intensity: number;
  direction?: 'positive' | 'negative';
}

export interface WordsByTrait {
  dominance: CategorizedWord[];
  extraversion: CategorizedWord[];
  patience: CategorizedWord[];
  formality: CategorizedWord[];
  objectivity: CategorizedWord[]; // kept for legacy compat
}

// Convert M3 words to Word format for compatibility (with direction preserved)
const M3_WORDS_CONVERTED = M3_ASSESSMENT_WORDS.map(word => ({
  id: word.id,
  translationKey: word.translationKey,
  category: word.category,
  intensity: word.intensity,
  direction: word.direction as 'positive' | 'negative'
}));

// Create a map for M3 direction lookup
const m3DirectionMap = new Map<string, 'positive' | 'negative'>();
M3_ASSESSMENT_WORDS.forEach(word => {
  m3DirectionMap.set(word.translationKey, word.direction);
});

// Combine all word lists for lookup (Word type for M1/M2, extended for M3)
const ALL_WORDS: (Word & { direction?: 'positive' | 'negative' })[] = [
  ...ASSESSMENT_WORDS,
  ...M2_ASSESSMENT_WORDS,
  ...M3_WORDS_CONVERTED
];

// Create a map for O(1) lookup by translation key
const wordMap = new Map<string, Word & { direction?: 'positive' | 'negative' }>();
ALL_WORDS.forEach(word => {
  wordMap.set(word.translationKey, word);
});

export function categorizeWordsByTrait(translationKeys: string[]): WordsByTrait {
  const result: WordsByTrait = {
    dominance: [],
    extraversion: [],
    patience: [],
    formality: [],
    objectivity: []
  };

  translationKeys.forEach(key => {
    const word = wordMap.get(key);
    if (word) {
      result[word.category].push({
        translationKey: key,
        intensity: word.intensity,
        direction: word.direction
      });
    }
  });

  // Sort each category by intensity (highest first)
  Object.keys(result).forEach(category => {
    result[category as keyof WordsByTrait].sort((a, b) => b.intensity - a.intensity);
  });

  return result;
}
